import { useMemo, useState } from 'react'
import { statType } from '../domain/statTypes'
import { periodLabel } from '../domain/periods'
import { formatClock } from '../lib/date'
import type { ID, PeriodFormat, StatEvent } from '../domain/types'

interface Props {
  events: StatEvent[]
  roster: { id: ID; name: string }[]
  periodFormat: PeriodFormat
  onDelete(eventId: ID): void
  onReassign(eventId: ID, playerId: ID): void
  onClose(): void
}

/**
 * Full event history for the current game. This is the correction surface: undo
 * handles the mis-tap you notice immediately, this handles the one you notice
 * three plays later.
 */
export function EventLog({ events, roster, periodFormat, onDelete, onReassign, onClose }: Props) {
  const [showDeleted, setShowDeleted] = useState(false)
  const nameOf = useMemo(() => new Map(roster.map((p) => [p.id, p.name])), [roster])

  const rows = useMemo(() => {
    const list = showDeleted ? events : events.filter((e) => !e.deleted)
    // Newest first: the row you want is almost always at the top.
    return list.slice().sort((a, b) => b.at - a.at)
  }, [events, showDeleted])

  const liveCount = events.filter((e) => !e.deleted).length

  return (
    <div className="overlay">
      <div className="overlay__panel">
        <header className="overlay__header">
          <h2>Event log <span className="count-badge">{liveCount}</span></h2>
          <label className="toggle">
            <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
            <span>Show deleted</span>
          </label>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Back to game
          </button>
        </header>

        <div className="log">
          {rows.length === 0 && <p className="muted">No events recorded yet.</p>}
          {rows.map((e) => {
            const def = statType(e.statType)
            return (
              <div key={e.id} className={'log__row' + (e.deleted ? ' log__row--deleted' : '')}>
                <span className="log__time">{formatClock(e.at)}</span>
                <span className="log__period">{periodLabel(periodFormat, e.period)}</span>
                <span className="log__stat" style={{ color: def.color }}>
                  {def.short}
                </span>
                <select
                  className="log__player"
                  value={e.playerId}
                  disabled={e.deleted}
                  onChange={(ev) => onReassign(e.id, ev.target.value)}
                >
                  {!nameOf.has(e.playerId) && <option value={e.playerId}>Unknown</option>}
                  {roster.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {e.deleted ? (
                  <span className="log__deleted">deleted</span>
                ) : (
                  <button type="button" className="btn btn--danger btn--sm" onClick={() => onDelete(e.id)}>
                    Delete
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
